// InvoiceManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { Button, message } from 'antd';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import { InvoiceTable, InvoiceModal } from './InvoiceCommon';
import SearchBar from './common/SearchBar';

const InvoiceManagement = ({ mode }) => {
  const [invoices, setInvoices] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [resourcesAccounts, setResourcesAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPurchaseModalVisible, setIsPurchaseModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [saleMaterials, setSaleMaterials] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchInvoices = useCallback(async (searchName = '') => {
    setLoading(true);
    try {
      const invoiceType = mode === 'sale' ? -1 : mode === 'purchase' ? 1 : null;
      const params = {};
      if (invoiceType !== null) params.Type = invoiceType;
      if (searchName) params.AccountName = searchName;

      const response = await axios.get(`${API_BASE_URL}Invoice/List`, { params });
      if (response.data.code === 0) {
        setInvoices(response.data.data);
      } else {
        message.error('فشل في جلب الفواتير');
      }
    } catch (error) {
      message.error('خطأ في جلب الفواتير');
    }
    setLoading(false);
  }, [mode]);

  useEffect(() => {
    fetchInvoices();
    mode === 'sale' ? fetchAccounts(12) : fetchAccounts(21);
  }, [fetchInvoices, mode]);

  useEffect(() => {
    fetchInvoices(searchTerm);
  }, [searchTerm]);

  const fetchAccounts = async (baseAccountId = null) => {
    try {
      const params = baseAccountId ? { BaseAccountId: baseAccountId } : {};
      const response = await axios.get(`${API_BASE_URL}Account/List`, { params });
      if (response.data.code === 0) {
        if (baseAccountId === 12) {
          setClientAccounts(response.data.data);
        } else if (baseAccountId === 21) {
          setResourcesAccounts(response.data.data);
        } else if (baseAccountId === 11) {
          setCashAccounts(response.data.data);
        }
      } else {
        message.error('فشل في جلب الحسابات');
      }
    } catch (error) {
      message.error('خطأ في جلب الحسابات');
    }
  };

  const fetchMaterials = async (searchName = '') => {
    try {
      const params = searchName ? { Name: searchName } : {};
      const response = await axios.get(`${API_BASE_URL}Material/List`, { params });
      if (response.data.code === 0) {
        setMaterials(response.data.data);
      } else {
        message.error('فشل في جلب المواد');
      }
    } catch (error) {
      message.error('خطأ في جلب المواد');
    }
  };

  const fetchSaleMaterials = async (searchName = '') => {
    try {
      const params = searchName ? { Name: searchName } : {};
      const response = await axios.get(`${API_BASE_URL}Material/Pick`, { params });
      if (response.data.code === 0) {
        setSaleMaterials(response.data.data);
      } else {
        message.error('فشل في جلب المواد المتاحة');
      }
    } catch (error) {
      message.error('خطأ في جلب المواد المتاحة');
    }
  };

  const fetchRepositories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}Repository/List`);
      if (response.data.code === 0) {
        setRepositories(response.data.data);
      } else {
        message.error('فشل في جلب المستودعات');
      }
    } catch (error) {
      message.error('خطأ في جلب المستودعات');
    }
  };

  const handleCreate = () => {
    setEditingInvoice(null);
    fetchAccounts(11);
    fetchMaterials();
    if (mode === 'purchase') {
      fetchAccounts(21);
      fetchRepositories();
    } else {
      fetchAccounts(12);
      fetchSaleMaterials();
    }
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}Invoice/GetById/${record.id}`);
      if (response.data.code === 0 && response.data.data) {
        setEditingInvoice(response.data.data);

        if (mode === 'purchase') {
          fetchAccounts(21);
          fetchMaterials();
          fetchRepositories();
        } else {
          fetchAccounts(12);
          fetchMaterials();
          fetchSaleMaterials();
        }

        setIsModalVisible(true);
      } else {
        message.error('فشل في جلب تفاصيل الفاتورة');
      }
    } catch (error) {
      message.error('خطأ في جلب تفاصيل الفاتورة');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}Invoice/Delete/${id}`);
      if (response.data.code === 0) {
        message.success('تم حذف الفاتورة بنجاح');
        fetchInvoices();
      } else {
        message.error('فشل في حذف الفاتورة');
      }
    } catch (error) {
      message.error('خطأ في حذف الفاتورة');
    }
  };

  const handleModalSuccess = async () => {
    await fetchInvoices();
    if (mode === 'sale') {
      await fetchSaleMaterials('');
    } else {
      await fetchMaterials('');
    }
  };

  const handleCreatePurchase = () => {
    fetchAccounts(11);
    fetchAccounts(21);
    fetchMaterials();
    fetchRepositories();
    setIsPurchaseModalVisible(true);
  };

  const handlePurchaseModalSuccess = async () => {
    await fetchInvoices();
    await fetchMaterials('');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" onClick={handleCreate}>
          إضافة فاتورة {mode === 'sale' ? 'بيع' : 'شراء'}
        </Button>
        <SearchBar
          onSearch={setSearchTerm}
          loading={searchLoading}
          placeholder="بحث باسم العميل / المورد"
        />
      </div>

      <InvoiceTable
        dataSource={invoices}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        accounts={mode === 'sale' ? clientAccounts : resourcesAccounts}
      />
      
      <InvoiceModal
        isVisible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={handleModalSuccess}
        editingInvoice={editingInvoice}
        mode={mode}
        clientAccounts={mode === 'sale' ? clientAccounts : resourcesAccounts}
        cashAccounts={cashAccounts}
        materials={materials}
        saleMaterials={saleMaterials}
        repositories={repositories}
        onFetchMaterials={fetchMaterials}
        onFetchSaleMaterials={fetchSaleMaterials}
        onCreatePurchase={handleCreatePurchase}
        onFetchAccounts={fetchAccounts}
      />

      <InvoiceModal
        isVisible={isPurchaseModalVisible}
        onCancel={() => setIsPurchaseModalVisible(false)}
        onSuccess={handlePurchaseModalSuccess}
        editingInvoice={null}
        mode="purchase"
        clientAccounts={resourcesAccounts}
        cashAccounts={cashAccounts}
        materials={materials}
        saleMaterials={saleMaterials}
        repositories={repositories}
        onFetchMaterials={fetchMaterials}
        onFetchSaleMaterials={fetchSaleMaterials}
        onFetchAccounts={fetchAccounts}
      />
    </div>
  );
};

export default InvoiceManagement;
